import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector, useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { markConceptGroupSelectionsAsReported } from '@verantos-dev/app-state-cohort-builder';
import {
  selectLatestStatus,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import {
  addResearchAssistantMessages,
  COHORT_ACTIONS,
  emitSyncConceptGroupSelections,
  selectResearchAssistantModel,
} from '@verantos-dev/app-state-research-assistant';
import { useAuth } from '@verantos-dev/core-auth';
import { getCohortsPath } from '@verantos-dev/core-constants';
import { avKeys } from '@verantos-dev/feature-analytical-variables';
import { emitUserSaveEvent } from '@verantos-dev/feature-research-assistant';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';
import { ResearchAssistantMessageEnumLegacy } from '@verantos-dev/types-common';

import {
  CohortBuilderActionDialogType,
  useCohortBuilderModalContext,
} from '../../contexts/CohortBuilderModalContext';
import { useCohortBuilderSaveQuery } from '../../hooks/common/useCohortBuilderSaveQuery';
import { useVersionNavigation } from '../../hooks/initialization/useVersionNavigation';
import { getCohortEditModalTitle } from '../../utils/getCohortEditModalTitle';

export const useSaveCohortDefinitionModal = () => {
  const [countDown, setCountDown] = useState(3);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const zustandLatestStatus = useCohortDefinitionStore(selectLatestStatus);
  // TODO: cleanup with explicit_save ff
  const isExplicitSave = useFeatureFlagSplit('explicit_save');
  const queryClient = useQueryClient();

  const researchAssistantModel = useAppSelector(selectResearchAssistantModel);
  const { closeDialog } = useCohortBuilderModalContext();

  const onClose = () => {
    closeDialog();
  };

  const {
    projectId,
    cohortVersionId,
    cohortId,
    cohortData,
    isInitialLoading,
    initialLoadingError,
    translationFailed,
    isSubmitting,
    submitVersion,
    submittingError,
    isSubmitted,
    isSubmitSuccess,
    isTranslated,
    submitResponseData,
  } = useCohortBuilderSaveQuery();

  const { handleVersionNavigation } = useVersionNavigation({
    projectId: projectId ?? '',
    cohortId: cohortId ?? '',
    cohortVersionId: cohortVersionId ?? '',
  });

  const effectiveInitialLoadingError = translationFailed
    ? {
        name: 'Translation Error',
        message: intl.formatMessage({
          id: 'projects.cohortBuilderPage.modal.translationFailed',
        }),
      }
    : initialLoadingError;

  const { user } = useAuth();

  const redirectToCohortList = useCallback(() => {
    projectId && navigate(getCohortsPath(projectId));
  }, [navigate, projectId]);

  const onSubmit = useCallback(() => {
    if (projectId && cohortId && cohortVersionId) {
      dispatch(markConceptGroupSelectionsAsReported({ markAll: true }));
      useCohortDefinitionStore
        .getState()
        .markConceptGroupSelectionsAsReported({ markAll: true, statuses: ['warning'] });
      emitSyncConceptGroupSelections({ cohortId, markAll: true });

      submitVersion();
      emitUserSaveEvent({
        model: researchAssistantModel,
        cohortVersionId,
        cohortId,
        projectId,
        user,
        cohortAction: COHORT_ACTIONS.SAVE,
      });
      dispatch(
        addResearchAssistantMessages([
          {
            message: intl.formatMessage({
              id: 'projects.cohortBuilderPage.researchAssistant.savedInfoMessage',
            }),
            timestamp: new Date().toISOString(),
            currentUser: 'user',
            isCurrentUser: true,
            type: ResearchAssistantMessageEnumLegacy.SAVE,
          },
        ])
      );
    }
  }, [
    cohortId,
    cohortVersionId,
    dispatch,
    intl,
    projectId,
    researchAssistantModel,
    submitVersion,
    user,
  ]);

  const modalLatestStatus = zustandLatestStatus;

  useEffect(() => {
    if (
      isTranslated &&
      !isInitialLoading &&
      modalLatestStatus &&
      [
        CohortDefinitionStatus.DRAFT,
        CohortDefinitionStatus.ERROR,
        CohortDefinitionStatus.UNSAVED,
      ].includes(modalLatestStatus) &&
      projectId &&
      cohortId &&
      !isSubmitted
    ) {
      onSubmit();
    }
  }, [
    modalLatestStatus,
    cohortId,
    onSubmit,
    projectId,
    isSubmitted,
    isInitialLoading,
    isTranslated,
  ]);

  // TODO: cleanup with explicit_save ff
  // On explicit_save: backend no longer emits cohort_saved, so the socket listener
  // won't fire. Replicate the same side-effects here keyed on the PUT/POST success,
  // including the silent URL/store version sync the socket path does on `versionChanged`
  // (see useCohortDefinitionSocket.ts) — a save from a PROCESSED/ERROR cohort mints a new
  // draft version, and without this the URL keeps pointing at the superseded version.
  useEffect(() => {
    if (!isExplicitSave || !isSubmitSuccess) return;
    const newVersionId = submitResponseData?.data?.cohortVersionId;
    if (newVersionId && newVersionId !== cohortVersionId) {
      handleVersionNavigation(newVersionId);
      useCohortDefinitionStore.setState((state) => ({
        profile: { ...state.profile, latestCohortVersionId: newVersionId },
      }));
    }
    useCohortDefinitionStore
      .getState()
      .resetOnSocketEvent({ newStatus: CohortDefinitionStatus.DRAFT, clearStagedChanges: true });
    useCohortDefinitionStore.getState().clearChangeStatuses();
    queryClient.invalidateQueries({ queryKey: avKeys.all });
  }, [
    isExplicitSave,
    isSubmitSuccess,
    submitResponseData,
    cohortVersionId,
    handleVersionNavigation,
    queryClient,
  ]);

  useEffect(() => {
    if (isSubmitSuccess && countDown > 0) {
      const interval = setInterval(() => {
        setCountDown((prevCount) => prevCount - 1);

        if (countDown === 1) {
          clearInterval(interval);
          closeDialog();
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [closeDialog, countDown, isSubmitSuccess, redirectToCohortList]);

  return {
    intl,
    title: getCohortEditModalTitle(
      intl,
      CohortBuilderActionDialogType.SAVE_COHORT_DEFINITION,
      true
    ),
    countDown,
    onClose,
    onSubmit,
    redirectToCohortList,
    cohortData,
    submittingError,
    isSubmitting,
    isInitialLoading,
    initialLoadingError: effectiveInitialLoadingError,
    isSubmitSuccess,
  };
};
